import React, { useState } from "react";
import { ExcelTemplateState } from "./model";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import CultureLabel from "components/Culture/CultureLabel";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import { ColumnsPanelForDetail } from "./ColumnsPanel/ColumnsPanel";

interface ExcelTemplateDetailSectionProps {
  excelTemplate: server.dto.ExcelTemplate
  availableMergeFieldTopics: server.dto.AvailableMergeFieldTopic[]
}

export const ExcelTemplateDetailSection = (props: ExcelTemplateDetailSectionProps) => {
  const [excelTemplate, setExcelTemplate] = useState<ExcelTemplateState>({
    title: props.excelTemplate.title,
    lcid: props.excelTemplate.lcid,
    distinctRows: props.excelTemplate.distinctRows,

    columns: props.excelTemplate.columns,
  });

  const culture = serverReference.templateCultures.find((c) => c.lcid === excelTemplate.lcid);

  const onValueChanged = (key: string, value: any) => {
    if (key === "title") {
      setExcelTemplate({ ...excelTemplate, title: value });
    } else if (key === "lcid") {
      setExcelTemplate({ ...excelTemplate, lcid: value });
    } else if (key === "distinctRows") {
      setExcelTemplate({ ...excelTemplate, distinctRows: value });
    }
  }

  const getUpdateExcelTemplateInstruction = (et: ExcelTemplateState): server.dto.UpdateExcelTemplateDetailsInstruction => ({
    excelTemplateId: props.excelTemplate.excelTemplateId,
    culture: et.lcid,
    title: et.title,
    distinctRows: et.distinctRows,
  });

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      type: "regular",
      key: "excelTemplate",
      title: RESX.ExcelTemplate.resxExcelTemplate,
      fields: [{
        key: "title",
        label: RESX.GeneralLabels.Title,
        render: () => <div>{excelTemplate.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.excelTemplate.maxTitleLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: excelTemplate.title,
          submitChange: (title: string) => window.CoreApi.ExcelTemplate.UpdateDetails({
            ...getUpdateExcelTemplateInstruction(excelTemplate),
            title,
          }),
        },
      }, {
        key: "lcid",
        label: RESX.Language.resxLanguage,
        render: () => <CultureLabel culture={culture} />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.LCID>) => <CulturesTemplateDropdown
            value={value}
            onChange={(c) => {
              onChange(c, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: excelTemplate.lcid,
          submitChange: (lcid: server.dto.LCID) => window.CoreApi.ExcelTemplate.UpdateDetails({
            ...getUpdateExcelTemplateInstruction(excelTemplate),
            culture: lcid,
          })
        },
      }, {
        key: "distinctRows",
        label: RESX.ExcelTemplate.UniqueRows,
        render: () => <YesNoText value={excelTemplate.distinctRows} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: excelTemplate.distinctRows,
          submitChange: (distinctRows: boolean) => window.CoreApi.ExcelTemplate.UpdateDetails({
            ...getUpdateExcelTemplateInstruction(excelTemplate),
            distinctRows
          }),
        },
      }]
    }, {
      type: "custom",
      key: "columns",
      render: () => <ColumnsPanelForDetail
        onColumnsUpdated={(columns) => setExcelTemplate({ ...excelTemplate, columns })}
        excelTemplateId={props.excelTemplate.excelTemplateId}
        columns={excelTemplate.columns}
        availableMergeFieldTopics={props.availableMergeFieldTopics}
      />
    }]}
  />
}
